import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ApiCall from "../../../Apicall/ApiCall";
import { useSignalR } from "../../../context/SignalRContext";
import PublicPage from "../../PublicPage";
import TransactionHeader from "./TransactionHeader";
import DealOwnershipStage from "./ownership/DealOwnershipStage";
import { DEAL_HOME_PATH, DEAL_COMPLETED_EVENT } from "../../lib/dealSession";

// The Ägarbyte stage on a page of its own — where the buyer lands once the seller has confirmed
// the payment, and where the seller's report of the transfer to Transportstyrelsen is awaited.
//
// Built exactly like DealPaymentPage, the stage before it: the same shell, the same header with
// the session countdown, the same deal-group join so the counterpart's sign-out and the "Leave
// the deal?" guard reach this page too. Only the stage inside differs.
export default function DealOwnershipPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await ApiCall({
          url: "/ContractMain/GetContractMainById",
          method: "GET",
          params: { Id: id },
        });
        const found = res?.data?.result || res?.result || null;
        if (!cancelled) {
          setDeal(found);
          setLoading(false);
        }
      } catch (_) {
        if (!cancelled) {
          setDeal(null);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Only a signed, uncancelled deal has an Ägarbyte stage at all. Whether the payment is far
  // enough along is the stage's own question, since it is the payment state that answers it.
  useEffect(() => {
    if (loading) return;
    if (!deal || !deal.dealComplete || deal.dealCancel) {
      navigate(DEAL_HOME_PATH, { replace: true });
    }
  }, [loading, deal, navigate]);

  const { dealId: connectedDealId, connectToDeal } = useSignalR();
  const joiningDeal = useRef(null);
  // The seller's report completes the sale and closes the live connection (DealSessionManager). This
  // page must not open it again just because it is now disconnected.
  const [finished, setFinished] = useState(false);
  useEffect(() => {
    const onCompleted = () => setFinished(true);
    window.addEventListener(DEAL_COMPLETED_EVENT, onCompleted);
    return () => window.removeEventListener(DEAL_COMPLETED_EVENT, onCompleted);
  }, []);
  useEffect(() => {
    // A finished sale has no live channel any more: nothing is joined for it.
    if (finished || !id || !deal?.dealComplete || deal?.dealCancel || deal?.dealState === "completed") return;
    if (String(connectedDealId) === String(id) || joiningDeal.current === id) return;
    joiningDeal.current = id;
    connectToDeal(id)
      .catch(() => {
        /* the stage still works; only the live deal-session rules are missed */
      })
      .finally(() => {
        joiningDeal.current = null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, deal, connectedDealId, finished]);

  return (
    <PublicPage>
      <div className="flex min-h-screen flex-col bg-ds-canvas">
        <TransactionHeader />
        <main className="ds-scope w-full flex-1 px-4 pb-16 pt-6 md:px-6">
          <div className="ds-form mx-auto w-full">
            {loading ? (
              // The stage says nothing until it knows where the deal stands; the shape is the
              // reference's, and the name of the stage being prepared is announced.
              <div className="mx-auto w-full" style={{ maxWidth: "42rem" }} role="status" aria-busy="true">
                <span className="sr-only">{t("ownership.preparing")}</span>
                <div className="border-t-2 border-ds-ink pt-4" aria-hidden="true">
                  <div className="h-6 w-28 animate-pulse rounded bg-[rgba(20,36,46,.08)]" />
                </div>
                <div className="mt-8 h-7 w-2/3 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
                <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-[rgba(20,36,46,.08)]" aria-hidden="true" />
              </div>
            ) : (
              <DealOwnershipStage contractId={id} onAdvance={(to) => navigate(to, { replace: true })} />
            )}
          </div>
        </main>
      </div>
    </PublicPage>
  );
}
